import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import type { TpaHandlerProvider, IPropsStore, Experiments } from '@wix/thunderbolt-symbols'
import {
	PageFeatureConfigSymbol,
	Props,
	SiteFeatureConfigSymbol,
	CompEventSymbol,
	ExperimentsSymbol,
} from '@wix/thunderbolt-symbols'
import type { INavigation } from 'feature-navigation'
import { NavigationSymbol } from 'feature-navigation'
import type { IRoutingLinkUtilsAPI } from 'feature-router'
import { RoutingLinkUtilsAPISymbol } from 'feature-router'
import _ from 'lodash'
import { createLinkUtils, isLinkProps } from '@wix/thunderbolt-commons'
import type { ILightboxUtils } from 'feature-lightbox'
import { LightboxUtilsSymbol } from 'feature-lightbox'
import { name } from '../symbols'
import type { TpaPageConfig } from '../types'
import type { PageLinkData, TpaPageLinkData } from '@wix/thunderbolt-becky-types'
import type { ImageZoomAPI } from 'feature-image-zoom'
import { ImageZoomAPISymbol } from 'feature-image-zoom'
import type { TpaCommonsSiteConfig } from 'feature-tpa-commons'
import { name as tpaCommonsName } from 'feature-tpa-commons'
import type { IMultilingualLinkUtilsAPI } from 'feature-multilingual'
import { MultilingualLinkUtilsAPISymbol } from 'feature-multilingual'
import type { ICustomUrlMapper } from 'feature-custom-url-mapper'
import { CustomUrlMapperSymbol } from 'feature-custom-url-mapper'

export type ReplaceSectionStateMessage = { state: string; queryParams?: Record<string, string> }
export type AppStateChangedMessage = { state: string }

export const ReplaceSectionStateHandler = withDependencies(
	[
		named(SiteFeatureConfigSymbol, tpaCommonsName),
		named(PageFeatureConfigSymbol, name),
		Props,
		NavigationSymbol,
		RoutingLinkUtilsAPISymbol,
		ExperimentsSymbol,
		optional(LightboxUtilsSymbol),
		optional(ImageZoomAPISymbol),
		optional(MultilingualLinkUtilsAPISymbol),
		optional(CustomUrlMapperSymbol),
	],
	(
		tpaSiteConfig: TpaCommonsSiteConfig,
		tpaPageConfig: TpaPageConfig,
		propsStore: IPropsStore,
		navigation: INavigation,
		routingLinkUtilsAPI: IRoutingLinkUtilsAPI,
		experiments: Experiments,
		lightboxUtils: ILightboxUtils,
		zoomAPI: ImageZoomAPI,
		multilingualLinkUtilsAPI: IMultilingualLinkUtilsAPI,
		customUrlMapper: ICustomUrlMapper
	): TpaHandlerProvider => ({
		getTpaHandlers() {
			const getLinkUtils = (routingInfo = routingLinkUtilsAPI.getLinkUtilsRoutingInfo()) => {
				const { metaSiteId, userFileDomainUrl, routersConfig, isMobileView, isPremiumDomain } = tpaSiteConfig
				return createLinkUtils({
					routingInfo,
					metaSiteId,
					userFileDomainUrl,
					routersConfig,
					popupPages: lightboxUtils?.getLightboxPages(),
					multilingualInfo: multilingualLinkUtilsAPI?.getMultilingualInfo(),
					isMobileView,
					isPremiumDomain,
					experiments,
				})
			}

			const replaceState = async ({
				compId,
				state,
				skipHistory,
				queryParams = {},
				disableScrollToTop = true,
			}: {
				compId: string
				state: string
				skipHistory: boolean
				disableScrollToTop?: boolean
				queryParams?: Record<string, string>
			}) => {
				const routingInfo = routingLinkUtilsAPI.getLinkUtilsRoutingInfo()
				const linkUtils = getLinkUtils(routingInfo)

				const { widgets } = tpaPageConfig
				const isTpaSection = widgets[compId]?.isSection
				if (!isTpaSection) {
					return
				}

				// current page link data
				const linkData = {
					type: 'TpaPageLink',
					pageId: routingInfo.pageId,
					path: state,
				} as TpaPageLinkData
				const linkUrl = linkUtils.getLinkUrlFromDataItem(linkData)

				const queryParamsUrl = _.isEmpty(queryParams)
					? ''
					: `?appSectionParams=${encodeURIComponent(JSON.stringify(queryParams))}`
				const url = `${linkUrl}${queryParamsUrl}`

				const linkProps = linkUtils.getLinkProps(url)
				await navigation.navigateTo(linkProps, { skipHistory, disableScrollToTop })
			}

			return {
				async replaceSectionState(_compId, { state, queryParams }: ReplaceSectionStateMessage): Promise<void> {
					return replaceState({ compId: _compId, skipHistory: true, state, queryParams })
				},
				async appStateChanged(_compId, { state }: AppStateChangedMessage): Promise<void> {
					// state can either be a string or a stringified cmd/args JSON such as "{"cmd":"zoom","args":[0]}"
					// used in TPA galleries
					let parsedState: { cmd: string; args?: Array<any> } | null
					try {
						parsedState = JSON.parse(state)
					} catch (e) {
						// state is not a JSON string
						parsedState = null
					}

					const triggerEventHandler = (handler: any, ...args: any) => {
						if (handler[CompEventSymbol]) {
							handler({
								args,
								compId: _compId,
							})
						} else {
							handler(...args)
						}
					}

					const triggerOnItemClick = (itemIndex: number) => {
						const onItemClicked = propsStore.get(_compId).onItemClicked
						onItemClicked &&
							triggerEventHandler(onItemClicked, {
								type: 'itemClicked',
								itemIndex,
								item: propsStore.get(_compId).images[itemIndex],
							})
					}

					// state is a stringified cmd/args JSON
					if (parsedState) {
						const tpaGalleryStateCommandToAction: Record<string, () => void> = {
							zoom: () => {
								const [itemIndex] = parsedState!.args!
								const { id } = propsStore.get(_compId).images[itemIndex]
								zoomAPI.openImageZoom(_compId, id)
								triggerOnItemClick(itemIndex)
							},
							itemClicked: () => {
								const [itemIndex] = parsedState!.args!
								triggerOnItemClick(itemIndex)
							},
							itemChanged: () => {
								const [itemIndex] = parsedState!.args!
								const onCurrentItemChanged = propsStore.get(_compId).onCurrentItemChanged
								onCurrentItemChanged &&
									triggerEventHandler(onCurrentItemChanged, {
										type: 'imageChanged',
										itemIndex,
										item: propsStore.get(_compId).images[itemIndex],
									})
							},
							componentReady: () => {
								const readyReportsCount = propsStore.get(_compId).readyReportsCount ?? 0
								propsStore.update({
									[_compId]: {
										componentReady: true,
										// In some cases when 'componentReady' was re-reported we need to re-trigger initialization steps, for example #TB-9591
										readyReportsCount: readyReportsCount + 1,
									},
								})
							},
							navigateToDynamicPage: () => {
								const [dynamicPageLinkData] = parsedState!.args!
								const linkUtils = getLinkUtils()
								const linkProps = isLinkProps(dynamicPageLinkData)
									? dynamicPageLinkData
									: linkUtils.getLinkProps(linkUtils.getLinkUrlFromDataItem(dynamicPageLinkData))
								navigation.navigateTo(linkProps)
							},
							navigateToAnchor: () => {
								const [pageId, anchorDataId] = parsedState!.args!
								const actualPageId =
									pageId === 'masterPage' ? routingLinkUtilsAPI.getLinkUtilsRoutingInfo().pageId : pageId
								const linkData = {
									type: 'PageLink',
									pageId: actualPageId,
									target: '_self',
								} as PageLinkData
								const linkUtils = getLinkUtils()
								const pageUrl = linkUtils.getLinkUrlFromDataItem(linkData)
								const linkProps = linkUtils.getLinkProps(pageUrl)
								const anchorLinkProps = Object.assign(linkProps, anchorDataId)
								navigation.navigateTo(anchorLinkProps)
							},
						}

						const commandAction = tpaGalleryStateCommandToAction[parsedState.cmd]
						if (commandAction) {
							commandAction()
						}
						return
					}

					// state is a regular string, need to invoke same logic as replaceSectionState handler
					// but when changing the url we should add it to browser history
					return replaceState({ compId: _compId, skipHistory: false, state })
				},
			}
		},
	})
)
